import { NgClass } from "@angular/common";
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from "@angular/core";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective } from "@ngbracket/ngx-layout/flex";
import { EventEmitterHelper, TypedChange } from "@tylertech/shared-module";
import { IInfoCardData, ISearchCriteria, } from "@tylertech/tyler-api-types";
import { Observable, Subject, Subscription } from "rxjs";
import { InfoCardCommandArgs, InfoCardTemplateContext } from "../../interfaces";
import { takeUntil } from "rxjs/operators";
import { InfoCardEditComponent } from "./infoCardEdit.component";
import { InfoCardViewComponent } from "./infoCardView.component";

@Component({
    selector: "info-card",
    templateUrl: "./infoCard.tmpl.html",
    imports: [
        ClassDirective,
        FlexDirective,
        InfoCardEditComponent,
        InfoCardViewComponent,
        NgClass,
    ]
})
export class InfoCardComponent implements OnInit, OnChanges
{
    private readonly destructor$ = new Subject<void>();
    private readonly refreshLayoutSubject = new Subject<void>();

    @Input() readonly item: IInfoCardData;
    @Output() private readonly itemChange = new EventEmitter<IInfoCardData>();

    /**
     * USAGES:
     * apps\glaccounts\src\components\manager\manager\manager.tmpl.html
     * library\personnel\src\components\employeeLookup\employeeLookup.tmpl.html
     */
    @Input() readonly criteria: ISearchCriteria<IInfoCardData>;

    @Input() readonly canDelete: boolean;
    /**
     * Only set to true for InfoCardCollections; which in turn is only used by:
     * apps\vendormanager\src\components\vendorManager.tmpl.html
     * library\incode9Shared\src\components\infoTableViewMetadata\infoTableViewMetadata.tmpl.html
     */
    @Input() readonly compact: boolean;
    @Input() readonly outlined: boolean;

    // TODO - Why would be allowed to enter the "Edit" mode if we can't save???
    @Input() readonly editable: boolean;
    // TODO - Why would be allowed to enter the "Edit" mode if we can't save???
    @Input() readonly saveable: boolean;

    @Input() readonly expand: boolean;
    @Input() readonly expandable: boolean;

    @Input()
    set refreshLayout(value: Observable<void>)
    {
        this.refreshLayoutSubscription?.unsubscribe();
        this.refreshLayoutSubscription = value.pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.refreshLayoutSubject.next();
            });
    }

    /** Rendered below the card header when the provider sets `CardHeaderDetailType` to `Custom`. */
    @Input() readonly headerTemplate: TemplateRef<InfoCardTemplateContext>;
    /** Rendered in the expanded card body when the provider sets `CardDetailType` to `Custom`. */
    @Input() readonly detailTemplate: TemplateRef<InfoCardTemplateContext>;

    @Output() private readonly clientAction = new EventEmitter<InfoCardCommandArgs>();
    @Output() private readonly refreshCollection = new EventEmitter();

    @ViewChild("cardWrapper") private readonly cardWrapper?: ElementRef<HTMLDivElement>;

    private readonly _refreshLayoutObservable: Observable<void>;
    private refreshLayoutSubscription: Subscription;
    editMode: boolean;
    hasClientActionFunction: boolean;

    constructor()
    {
        this._refreshLayoutObservable = this.refreshLayoutSubject.asObservable();
    }

    ngOnInit(): void
    {
        this.hasClientActionFunction = EventEmitterHelper.hasSubscribers(this.clientAction);
    }

    get cardClass(): string { return this.item?.CardCssStyle || ""; }
    get maxWidth(): number { return this.cardWrapper?.nativeElement?.clientWidth; }
    get refreshLayoutObservable(): Observable<void> { return this._refreshLayoutObservable; }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (changes.item)
        {
            this.refreshLayoutSubject.next();
        }

        const itemChanged = this.hasInputChanged(changes.item);
        const criteriaChanged = this.hasInputChanged(changes.criteria);
        if (itemChanged || criteriaChanged)
        {
            this.refreshLayoutSubject.next();
        }
    }

    private hasInputChanged(change: TypedChange<unknown> | undefined): boolean
    {
        if (!change)
        {
            return false;
        }

        if (!change.previousValue && !change.currentValue)
        {
            return false;
        }

        if (!change.previousValue && change.currentValue)
        {
            return true;
        }

        if (change.previousValue && !change.currentValue)
        {
            return true;
        }

        return JSON.stringify(change.previousValue) !== JSON.stringify(change.currentValue);
    }

    doClientAction($event: InfoCardCommandArgs): void
    {
        this.clientAction.emit($event);
    }

    onItemChange($event: IInfoCardData): void
    {
        this.itemChange.emit($event);
    }

    onEdit(): void
    {
        this.editMode = true;
    }

    onSave($event: IInfoCardData): void
    {
        this.editMode = false;
        this.itemChange.emit($event);
    }

    onCancel(): void
    {
        this.editMode = false;
    }

    onRefresh(): void
    {
        this.refreshCollection.emit();
    }
}
